import { useEffect, useRef, useState } from "react";
import api from "../api";
import { openComposerWindow } from "../composerWindow";
import RichEditor from "./RichEditor";
import { Modal } from "./ui";
import { AiGenForm, AttachmentChips, EventForm } from "./EmailComposerForms";

const TITLES = { reply: "Rispondi", replyall: "Rispondi a tutti", forward: "Inoltra", new: "Nuova email" };

const KIND_ICO = { azienda: "🏢", lead: "⭐", contatto: "👤", corrispondenza: "✉️" };

function RecipientInput({ label, value, onChange, placeholder, testid }) {
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const [fix, setFix] = useState(null); // correggi mittente: {oldEmail, first_name, last_name, email, busy}
  const timer = useRef(null);

  const handle = (v) => {
    onChange(v);
    const last = v.split(/[,;]/).pop().trim();
    clearTimeout(timer.current);
    if (last.length < 2 || last.includes("@")) { setSugg([]); setOpen(false); return; }
    timer.current = setTimeout(() => {
      api.get(`/emails/recipients-suggest?q=${encodeURIComponent(last)}`)
        .then((r) => { setSugg(r.data); setOpen(r.data.length > 0); })
        .catch(() => {});
    }, 250);
  };

  const pick = (s) => {
    const parts = value.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
    parts.pop();
    onChange([...parts, s.email].join(", "));
    setOpen(false); setSugg([]);
  };

  return (
    <div className="field" style={{ position: "relative" }}>
      <label>{label}</label>
      <input value={value} onChange={(e) => handle(e.target.value)} placeholder={placeholder}
        onBlur={() => setTimeout(() => setOpen(false), 150)} autoComplete="off" data-testid={testid} />
      {open && sugg.length > 0 && (
        <div className="ac-list" data-testid={`${testid}-suggestions`}>
          {sugg.map((s) => (
            <div key={s.email} className="ac-item" onMouseDown={() => pick(s)} data-testid={`${testid}-opt-${s.email}`}>
              <span className="ac-ico">{KIND_ICO[s.kind] || "👤"}</span>
              <span className="ac-label">{s.name || s.email}</span>
              <span className="ac-email">{s.email}</span>
              {s.kind === "corrispondenza" && (
                <>
                  <button type="button" className="btn xs" title="Correggi l'indirizzo e salvalo in anagrafica"
                    style={{ marginLeft: 6, padding: "0 6px", flexShrink: 0 }}
                    onMouseDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      const parts = (s.name || "").split(" ").filter(Boolean);
                      setFix({ oldEmail: s.email, first_name: parts[0] || "", last_name: parts.slice(1).join(" "), email: s.email });
                      setOpen(false);
                    }} data-testid={`${testid}-fix-${s.email}`}>✏️</button>
                  <button type="button" className="btn xs" title="Non suggerire più questo indirizzo (preso dai messaggi ricevuti)"
                    style={{ marginLeft: 4, padding: "0 6px", flexShrink: 0 }}
                    onMouseDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      api.post("/emails/recipients-suggest/suppress", { email: s.email }).catch(() => {});
                      setSugg((list) => list.filter((x) => x.email !== s.email));
                    }} data-testid={`${testid}-suppress-${s.email}`}>✕</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {fix && (
        <Modal title="✏️ Correggi e salva in anagrafica" onClose={() => setFix(null)}>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Questo indirizzo viene dai messaggi ricevuti. Correggilo e salvalo come contatto:
            d'ora in poi verrà suggerito dall'anagrafica (👤) e quello sbagliato non apparirà più.
          </p>
          <div className="grid-2">
            <div className="field"><label>Nome</label>
              <input value={fix.first_name} onChange={(e) => setFix({ ...fix, first_name: e.target.value })} data-testid="fix-first-name" /></div>
            <div className="field"><label>Cognome</label>
              <input value={fix.last_name} onChange={(e) => setFix({ ...fix, last_name: e.target.value })} data-testid="fix-last-name" /></div>
          </div>
          <div className="field"><label>Email corretta</label>
            <input value={fix.email} onChange={(e) => setFix({ ...fix, email: e.target.value })} data-testid="fix-email" /></div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setFix(null)}>Annulla</button>
            <button type="button" className="btn primary" disabled={fix.busy || !fix.email.includes("@")} data-testid="fix-save"
              onClick={async () => {
                setFix({ ...fix, busy: true });
                try {
                  await api.post("/persons", { first_name: fix.first_name.trim(), last_name: fix.last_name.trim(), email: fix.email.trim().toLowerCase() });
                  if (fix.email.trim().toLowerCase() !== fix.oldEmail) {
                    await api.post("/emails/recipients-suggest/suppress", { email: fix.oldEmail }).catch(() => {});
                  }
                  pick({ email: fix.email.trim().toLowerCase() });
                  setFix(null);
                } catch (e) {
                  alert("Errore: " + (e?.response?.data?.detail || e.message));
                  setFix((f) => f && ({ ...f, busy: false }));
                }
              }}>{fix.busy ? "Salvo…" : "Salva contatto e usa"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function parseList(s) {
  return (Array.isArray(s) ? s.join(", ") : s || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
}
const asStr = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const textToHtml = (t) => (t || "").split("\n").map((l) => l.trim() === "" ? "<div><br></div>" : `<div>${l.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</div>`).join("");
const htmlToText = (h) => { const d = document.createElement("div"); d.innerHTML = h || ""; return d.innerText.trim(); };
// Uniforma l'HTML in uscita: font coerente in tutti i client e <p> senza margini
// giganti (i client email danno margine 1em ai <p>, mentre le righe <div> no →
// "prime righe con formattazione diversa dal resto").
const wrapOutgoing = (h) => {
  if (!h) return h;
  const norm = h.replace(/<p(?![a-z])(?![^>]*style=)/gi, '<p style="margin:0 0 10px"');
  return `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#1f2430">${norm}</div>`;
};

export default function EmailComposer({ mode = "new", original = null, prefillTo = "", prefillSubject = "", prefillBody = "", draft = null, scheduled = null, onClose, onSent }) {
  const [draftId, setDraftId] = useState(draft?.id || null);
  const [senders, setSenders] = useState([]);
  const [mailboxId, setMailboxId] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [loadingAtts, setLoadingAtts] = useState(false);
  const [origQuote, setOrigQuote] = useState("");
  const [includeOrig, setIncludeOrig] = useState(true);
  const [aiHtml, setAiHtml] = useState(null);
  const [genFormat, setGenFormat] = useState("text");
  const [aiText, setAiText] = useState("");
  const [aiInstr, setAiInstr] = useState("");
  const [aiBrief, setAiBrief] = useState("");
  const [showGen, setShowGen] = useState(false);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [msgErr, setMsgErr] = useState(false);
  const msgRef = useRef(null);
  // l'esito dell'invio non deve mai passare inosservato: banner + scroll sul messaggio
  const flash = (text, isErr = false) => {
    setMsg(text);
    setMsgErr(isErr);
    setTimeout(() => msgRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 60);
  };
  const sendError = (e) => {
    const st = e.response?.status;
    if (st === 413) return "la mail è troppo pesante (allegati oltre il limite consentito). Togli o alleggerisci gli allegati e riprova.";
    if (st === 401) return "la sessione è scaduta. Rientra nel CRM e riprova.";
    if (e.code === "ECONNABORTED") return "il server non ha risposto in tempo. Controlla in «Inviate» se è partita lo stesso, prima di rimandarla.";
    if (!e.response) return "nessuna risposta dal server (connessione interrotta). Controlla in «Inviate» se è partita lo stesso.";
    return e.response?.data?.detail || e.message;
  };
  const pendingQuote = useRef("");
  // evento calendario
  const [showEvent, setShowEvent] = useState(false);
  const [evDate, setEvDate] = useState("");
  const [evTime, setEvTime] = useState("");
  const [evDur, setEvDur] = useState(30);
  // invio programmato
  const [schedAt, setSchedAt] = useState("");
  const [showSched, setShowSched] = useState(!!scheduled);
  // alcune bozze/programmate hanno i destinatari salvati come elenco: va sempre letto come testo
  // quando è già aperto nella finestra separata non serve il pulsante per staccarlo
  const inWindow = window.location.pathname === "/scrivi";
  const toStr = Array.isArray(to) ? to.join(", ") : String(to || "");

  useEffect(() => {
    if (!scheduled) return;
    setTo(asStr(scheduled.to));
    const ccS = (scheduled.cc || []).join(", "), bccS = (scheduled.bcc || []).join(", ");
    setCc(ccS); setBcc(bccS);
    if (ccS || bccS) setShowCc(true);
    setSubject(scheduled.subject || "");
    setBody(scheduled.body_editor || "");
    if (scheduled.ai_html) { setAiHtml(scheduled.ai_html); setAiText(scheduled.body_text || ""); }
    setAttachments(scheduled.attachments || []);
    const d = new Date(scheduled.scheduled_at);
    const pad = (n) => String(n).padStart(2, "0");
    setSchedAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }, [scheduled]); // eslint-disable-line

  useEffect(() => {
    api.get("/email-templates/senders").then((r) => {
      setSenders(r.data);
      const def = r.data.find((s) => s.default) || r.data[0];
      const mid = (scheduled?.mailbox_id) || (draft?.mailbox_id) || (original?.mailbox_id) || def?.id || "";
      setMailboxId(mid);
      if (mode === "replyall" && original) {
        // Rispondi a tutti: in copia anche gli altri destinatari (escluse le nostre caselle)
        const own = r.data.map((s) => (s.address || "").toLowerCase()).filter(Boolean);
        const others = [...new Set([...(original.to || []), ...(original.cc || [])]
          .map((a) => (a || "").trim().toLowerCase())
          .filter((a) => a && !own.includes(a) && a !== (original.from_email || "").toLowerCase()))];
        if (others.length) { setCc(others.join(", ")); setShowCc(true); }
      }
      return mid;
    }).catch(() => "").then((mid) => {
      if (draft) return;
      // corpo: spazio per scrivere + firma (della casella) + messaggio originale citato sotto
      api.get(`/emails/signature${mid ? `?mailbox_id=${mid}` : ""}`).then((r) => {
        const sig = r.data.signature || "";
        const sigHtml = sig ? (r.data.is_html ? sig : textToHtml(sig)) : "";
        setBody((prefillBody || "<div><br></div>") + `<div id="sig-block">${sigHtml}</div>` + (pendingQuote.current ? "<div><br></div>" + pendingQuote.current : ""));
      }).catch(() => setBody((prefillBody || "<div><br></div>") + (pendingQuote.current || "")));
    });

    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const quotedHtml = () => {
      const src = original?.body_html || textToHtml(original?.body_text || "");
      const when = original?.received_at ? new Date(original.received_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "";
      return `<p>Il ${when}, ${esc(original?.from_name)} &lt;${esc(original?.from_email)}&gt; ha scritto:</p>` +
        `<blockquote style="border-left:3px solid #94a3b8;margin:6px 0 0;padding:4px 12px;color:#64748b;">${src}</blockquote>`;
    };
    let quote = "";
    if ((mode === "reply" || mode === "replyall") && original) {
      setTo(original.from_email || "");
      setSubject((/^re:/i.test(original.subject || "") ? "" : "Re: ") + (original.subject || ""));
      quote = quotedHtml();
    } else if (mode === "forward" && original) {
      setSubject("Fwd: " + (original.subject || ""));
      quote = `<p>---------- Messaggio inoltrato ----------<br>Da: ${esc(original.from_name)} &lt;${esc(original.from_email)}&gt;<br>Oggetto: ${esc(original.subject)}</p>` +
        (original.body_html || textToHtml(original.body_text || ""));
    } else if (mode === "new" && prefillTo) {
      setTo(prefillTo);
    }
    if (mode === "new" && prefillSubject) setSubject(prefillSubject);
    if (quote) quote = `<div id="orig-quote">${quote}</div>`;
    setOrigQuote(quote);
    setIncludeOrig(true);
    pendingQuote.current = quote;
    // Inoltro: recupera gli allegati della email originale (rimovibili con la ✕)
    if (mode === "forward" && original?.id) {
      (async () => {
        setLoadingAtts(true);
        const got = [];
        try {
          const lr = await api.get(`/emails/${original.id}/attachments`);
          for (const a of lr.data || []) {
            try {
              const br = await api.get(`/emails/${original.id}/attachments/${a.index}`, { responseType: "blob" });
              const b64 = await new Promise((res) => {
                const fr = new FileReader();
                fr.onload = () => res(String(fr.result).split(",")[1]);
                fr.readAsDataURL(br.data);
              });
              got.push({
                filename: a.filename || `allegato-${(a.index ?? 0) + 1}`,
                content_type: a.content_type || br.data.type || "application/octet-stream",
                content_b64: b64, size: a.size || br.data.size,
              });
            } catch (_) { /* singolo allegato non recuperabile */ }
          }
          setAttachments((prev) => [...got, ...prev.filter((x) => !got.some((g) => g.filename === x.filename && g.size === x.size))]);
        } catch (_) {}
        setLoadingAtts(false);
      })();
    }
    // riprendi bozza: prefill completo e niente firma automatica
    if (draft) {
      setTo(asStr(draft.to));
      setCc(asStr(draft.cc));
      setBcc(asStr(draft.bcc));
      if (draft.cc || draft.bcc) setShowCc(true);
      setSubject(draft.subject || "");
      setBody(draft.body_text || "<div><br></div>");
      if (draft.id) {
        api.get(`/emails/drafts/${draft.id}`)
          .then((r) => setAttachments((r.data.attachments || []).filter((a) => a.content_b64)))
          .catch(() => {});
      }
      return;
    }
  }, [mode, original, prefillTo, draft]);

  const changeMailbox = (mid) => {
    setMailboxId(mid);
    api.get(`/emails/signature${mid ? `?mailbox_id=${mid}` : ""}`).then((r) => {
      const sig = r.data.signature || "";
      const sigHtml = sig ? (r.data.is_html ? sig : textToHtml(sig)) : "";
      setBody((cur) => {
        const div = document.createElement("div");
        div.innerHTML = cur || "";
        const blk = div.querySelector("#sig-block");
        if (blk) { blk.innerHTML = sigHtml; return div.innerHTML; }
        return cur;
      });
    }).catch(() => {});
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.readAsDataURL(f);
      });
      setAttachments((a) => [...a, { filename: f.name, content_type: f.type, content_b64: b64, size: f.size }]);
      dirtyRef.current = true;
    }
    e.target.value = "";
  };
  const removeAttachment = (i) => { dirtyRef.current = true; setAttachments((a) => a.filter((_, idx) => idx !== i)); };

  const saveDraft = async () => {
    setBusy("draft");
    try {
      const r = await api.post("/emails/drafts", { id: draftId || undefined, to: toStr, cc, bcc, subject, body_text: body, reply_to_id: original?.id || draft?.reply_to_id || null, attachments, mailbox_id: mailboxId || null });
      setDraftId(r.data.id);
      lastSavedRef.current = draftSnapshot();
      setMsg(`Bozza salvata${attachments.length ? ` con ${attachments.length} allegat${attachments.length === 1 ? "o" : "i"}` : ""}. La ritrovi in Da gestire → 📝 Bozze.`);
      return r.data.id;
    } catch (e) { setMsg("Errore bozza: " + (e.response?.data?.detail || e.message)); return null; }
    finally { setBusy(""); }
  };

  // ↗ stacca la mail in una finestra separata: salva la bozza e la riapre in un popup
  const popOut = async () => {
    const id = await saveDraft();
    if (!id) return;
    openComposerWindow({ draft: id });
    onSent && onSent();
    onClose();
  };

  const deleteDraft = async () => {
    if (!draftId) return;
    if (!window.confirm("Eliminare definitivamente questa bozza?")) return;
    setBusy("deldraft");
    try {
      await api.delete(`/emails/drafts/${draftId}`);
      onSent && onSent();
      onClose();
    } catch (e) { setMsg("Errore eliminazione bozza: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  // ── salvataggio automatico ogni 30 secondi mentre si scrive ──
  const stateRef = useRef({});
  const lastSavedRef = useRef(null);
  const dirtyRef = useRef(false);
  const draftSnapshot = () => {
    const s = stateRef.current;
    return JSON.stringify([s.to, s.cc, s.bcc, s.subject, s.body, (s.attachments || []).map((a) => a.filename + (a.size || 0)), s.mailboxId]);
  };
  useEffect(() => { stateRef.current = { to, cc, bcc, subject, body, attachments, mailboxId, draftId }; });
  useEffect(() => {
    if (scheduled) return undefined;
    const t = setInterval(async () => {
      const s = stateRef.current;
      const snap = draftSnapshot();
      const hasContent = (s.to || "").trim() || (s.subject || "").trim() || (s.attachments || []).length > 0;
      if (!dirtyRef.current || !hasContent || snap === lastSavedRef.current) return;
      try {
        const r = await api.post("/emails/drafts", {
          id: s.draftId || undefined, to: s.to, cc: s.cc, bcc: s.bcc, subject: s.subject,
          body_text: s.body, reply_to_id: original?.id || draft?.reply_to_id || null, attachments: s.attachments, mailbox_id: s.mailboxId || null,
        });
        setDraftId(r.data.id);
        lastSavedRef.current = snap;
        setMsg(`💾 Bozza salvata automaticamente alle ${new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}${(s.attachments || []).length ? ` (${s.attachments.length} allegat${s.attachments.length === 1 ? "o" : "i"})` : ""}.`);
      } catch (_) { /* riprova al prossimo giro */ }
    }, 30000);
    return () => clearInterval(t);
  }, [scheduled]); // eslint-disable-line

  const onBodyChange = (v) => { dirtyRef.current = true; setBody(v); if (aiHtml) setAiHtml(null); };

  const toggleOrig = () => {
    if (includeOrig) {
      const div = document.createElement("div");
      div.innerHTML = body || "";
      const q = div.querySelector("#orig-quote");
      if (q) q.remove();
      setBody(div.innerHTML);
      setIncludeOrig(false);
    } else {
      setBody((body || "") + "<div><br></div>" + origQuote);
      setIncludeOrig(true);
    }
  };

  const improve = async () => {
    if (!htmlToText(body).trim()) { setMsg("Scrivi prima una bozza."); return; }
    setBusy("ai"); setMsg("Claude sta migliorando la bozza…");
    try {
      const r = await api.post("/emails/ai-improve", { text: htmlToText(body), instruction: aiInstr });
      setBody(r.data.text);
      setMsg("Bozza migliorata.");
    } catch (e) { setMsg("Errore AI: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  const generate = async () => {
    if (!aiBrief.trim()) { setMsg("Scrivi cosa deve dire la mail."); return; }
    setBusy("gen"); setMsg("Claude sta creando l'email…");
    try {
      const r = await api.post("/emails/ai-generate", { brief: aiBrief, subject_hint: subject, format: genFormat });
      if (genFormat === "text") {
        // testo normale: entra nel corpo dell'editor, prima della firma e della citazione
        const gen = r.data.html || "";
        setBody((cur) => {
          const div = document.createElement("div");
          div.innerHTML = cur || "";
          const sig = div.querySelector("#sig-block");
          const q = div.querySelector("#orig-quote");
          return gen + "<div><br></div>" +
            (sig ? `<div id="sig-block">${sig.innerHTML}</div>` : "") +
            (q ? `<div><br></div><div id="orig-quote">${q.innerHTML}</div>` : "");
        });
        setAiHtml(null);
        if (r.data.subject && !subject.trim()) setSubject(r.data.subject);
        setShowGen(false);
        setMsg("Testo generato: lo trovi nel corpo del messaggio, modificalo pure.");
      } else {
        setAiHtml(r.data.html || "");
        setAiText(r.data.text || "");
        if (r.data.subject) setSubject(r.data.subject);
        setShowGen(false);
        setMsg("Email generata. Controlla l'anteprima.");
      }
    } catch (e) { setMsg("Errore AI: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  const basePayload = () => ({
    to: toStr.trim(),
    cc: parseList(cc),
    bcc: parseList(bcc),
    subject,
    body_text: aiHtml ? aiText : htmlToText(body),
    body_html: aiHtml || (body ? wrapOutgoing(body) : null),
    mailbox_id: mailboxId || null,
    attachments: attachments.length ? attachments : null,
  });

  const send = async () => {
    if (!toStr.trim()) { flash("Indica almeno un destinatario.", true); return; }
    setBusy("send"); flash("Sto inviando la mail…");
    try {
      await api.post("/emails/compose", {
        ...basePayload(),
        reply_to_id: original?.id || draft?.reply_to_id || null,
        thread_id: (mode === "reply" || mode === "replyall") ? (original?.thread_id || null) : null,
      }, { timeout: 120000 });
      if (draftId) { try { await api.delete(`/emails/drafts/${draftId}`); } catch (_) {} }
      onSent && onSent();
      onClose();
    } catch (e) {
      flash("Non è partita: " + sendError(e), true);
      setBusy("");
    }
  };

  const schedule = async () => {
    if (!toStr.trim()) { flash("Indica almeno un destinatario.", true); return; }
    if (!schedAt) { flash("Indica data e ora di invio.", true); return; }
    const when = new Date(schedAt);
    if (!(when > new Date())) { flash("La data di invio deve essere nel futuro.", true); return; }
    setBusy("sched"); setMsg("");
    try {
      const payload = { ...basePayload(), scheduled_at: when.toISOString(), body_editor: aiHtml ? null : (body || null), ai_html: aiHtml || null };
      if (scheduled) await api.put(`/emails/scheduled/${scheduled.id}`, payload);
      else await api.post("/emails/scheduled", payload);
      if (draftId) { try { await api.delete(`/emails/drafts/${draftId}`); } catch (_) {} }
      onSent && onSent();
      onClose();
    } catch (e) {
      flash("Non è stata programmata: " + sendError(e), true);
      setBusy("");
    }
  };

  const createEvent = async () => {
    if (!evDate || !evTime) { setMsg("Indica data e ora dell'evento."); return; }
    setBusy("event");
    try {
      await api.post("/calendar/events", {
        summary: subject || "Appuntamento",
        description: body || "",
        start: `${evDate}T${evTime}:00`,
        duration_min: Number(evDur) || 30,
        attendees: toStr.trim() ? [toStr.trim()] : [],
      });
      setMsg("Evento creato su Google Calendar.");
      setShowEvent(false);
    } catch (e) { setMsg("Errore evento: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(""); }
  };

  const printEmail = () => {
    const content = aiHtml || `<pre style="white-space:pre-wrap;font-family:sans-serif">${(body || "").replace(/</g, "&lt;")}</pre>`;
    const w = window.open("", "_blank");
    if (!w) { setMsg("Abilita i popup per stampare/salvare in PDF."); return; }
    w.document.write(`<html><head><title>${subject || "Email"}</title></head><body>
      <h2 style="font-family:sans-serif">${subject || "(senza oggetto)"}</h2>
      <p style="font-family:sans-serif;color:#555">A: ${to}${cc ? " · CC: " + cc : ""}</p><hr/>${content}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Modal title={scheduled ? "Modifica email programmata" : TITLES[mode]} onClose={onClose} wide>
      <div className="field"><label>Da (casella)</label>
        <select value={mailboxId} onChange={(e) => changeMailbox(e.target.value)} data-testid="composer-from">
          {senders.length === 0 && <option value="">— nessuna casella collegata —</option>}
          {senders.map((s) => <option key={s.id} value={s.id}>{s.address}</option>)}
        </select>
      </div>
      <RecipientInput label="A *" value={toStr} onChange={(v) => { dirtyRef.current = true; setTo(v); }} placeholder="Nome o indirizzo… (es. Rossi)" testid="composer-to" />
      {!showCc && (
        <p style={{ margin: "0 0 8px" }}>
          <button type="button" className="btn sm" onClick={() => setShowCc(true)} data-testid="composer-toggle-cc">+ CC / CCN</button>
        </p>
      )}
      {showCc && (
        <>
          <RecipientInput label="CC" value={cc} onChange={(v) => { dirtyRef.current = true; setCc(v); }} placeholder="Nome o indirizzo…" testid="composer-cc" />
          <RecipientInput label="CCN (BCC)" value={bcc} onChange={(v) => { dirtyRef.current = true; setBcc(v); }} placeholder="Nome o indirizzo…" testid="composer-bcc" />
        </>
      )}
      <div className="field"><label>Oggetto</label>
        <input value={subject} onChange={(e) => { dirtyRef.current = true; setSubject(e.target.value); }} data-testid="composer-subject" />
      </div>

      {aiHtml ? (
        <div className="field"><label>Anteprima email generata (Claude)</label>
          <iframe title="composer-preview" srcDoc={aiHtml} data-testid="composer-preview" sandbox=""
            style={{ width: "100%", height: 300, border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, background: "#fff" }} />
          <p style={{ marginTop: 6, display: "flex", gap: 8 }}>
            <button type="button" className="btn sm" onClick={() => setAiHtml(null)} data-testid="composer-edit-text">← Torna all'editor</button>
            <button type="button" className="btn sm" onClick={() => setShowGen(true)} data-testid="composer-regen">↻ Rigenera</button>
          </p>
        </div>
      ) : (
        <div className="field"><label>Messaggio</label>
          <RichEditor html={body} onChange={onBodyChange} testid="composer-body" minHeight={220} />
          {origQuote && (
            <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 6, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={includeOrig} onChange={toggleOrig} data-testid="composer-include-orig" style={{ margin: 0 }} />
              Includi il messaggio originale in fondo (puoi modificarlo direttamente nell'editor)
            </label>
          )}
        </div>
      )}

      {showGen && !aiHtml && (
        <AiGenForm aiBrief={aiBrief} setAiBrief={setAiBrief} genFormat={genFormat} setGenFormat={setGenFormat} onGenerate={generate} onCancel={() => setShowGen(false)} busy={busy} />
      )}

      <div className="toolbar" style={{ gap: 6, flexWrap: "wrap" }}>
        <label className="btn" style={{ cursor: "pointer" }} data-testid="composer-attach-label">
          📎 Allega file
          <input type="file" multiple style={{ display: "none" }} onChange={onFiles} data-testid="composer-attach-input" />
        </label>
        {!aiHtml && <>
          <input style={{ maxWidth: 200 }} placeholder="Istruzione AI (es. più formale)" value={aiInstr} onChange={(e) => setAiInstr(e.target.value)} data-testid="composer-ai-instr" />
          <button type="button" className="btn" onClick={improve} disabled={busy === "ai"} data-testid="composer-ai-btn">{busy === "ai" ? "…" : "✨ Migliora con AI"}</button>
        </>}
        <button type="button" className="btn" onClick={() => setShowGen((v) => !v)} data-testid="composer-gen-toggle">🤖 Crea con Claude</button>
        <button type="button" className="btn" onClick={() => setShowEvent((v) => !v)} data-testid="composer-event-btn">📅 Crea evento</button>
        <button type="button" className="btn" onClick={printEmail} data-testid="composer-print-btn">🖨 Stampa / PDF</button>
      </div>

      <AttachmentChips attachments={attachments} onRemove={removeAttachment} />
      {loadingAtts && <p className="muted" style={{ margin: "6px 0 0", fontSize: 13 }} data-testid="composer-atts-loading">📎 Sto recuperando gli allegati della email originale…</p>}
      {!loadingAtts && mode === "forward" && attachments.length > 0 && (
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 12.5 }} data-testid="composer-atts-note">Allegati originali inclusi nell'inoltro: rimuovili con la ✕ se non servono.</p>
      )}

      {showEvent && (
        <EventForm evDate={evDate} setEvDate={setEvDate} evTime={evTime} setEvTime={setEvTime}
          evDur={evDur} setEvDur={setEvDur} onCreate={createEvent} busy={busy} />
      )}

      {msg && (
        <p ref={msgRef} data-testid="composer-msg"
          style={msgErr
            ? { marginTop: 8, padding: "10px 12px", borderRadius: 10, border: "1px solid #c46b66",
                background: "rgba(196,107,102,.14)", color: "#ffb3ae", fontSize: 14, fontWeight: 600 }
            : { marginTop: 8, padding: "9px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,.14)",
                background: "rgba(255,255,255,.05)", fontSize: 13.5 }}>
          {msgErr ? "⚠️ " : ""}{msg}
        </p>
      )}

      <div className="modal-actions" style={{ flexWrap: "wrap" }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        {!scheduled && <button type="button" className="btn" onClick={saveDraft} disabled={busy === "draft"} data-testid="composer-draft-btn">{busy === "draft" ? "…" : "💾 Salva bozza"}</button>}
        {!scheduled && !inWindow && (
          <button type="button" className="btn" onClick={popOut} disabled={busy === "draft"} data-testid="composer-popout-btn" title="Salva la bozza e la riapre in una finestra separata: puoi girare nel CRM mentre scrivi">
            ↗ Finestra separata
          </button>
        )}
        {!scheduled && draftId && (
          <button type="button" className="btn danger" onClick={deleteDraft} disabled={busy === "deldraft"} data-testid="composer-draft-delete-btn">
            {busy === "deldraft" ? "…" : "🗑 Elimina bozza"}
          </button>
        )}
        <button type="button" className="btn" onClick={() => setShowSched((v) => !v)} data-testid="composer-sched-toggle">🕐 {scheduled ? "Quando inviarla" : "Programma invio"}</button>
        {showSched && (
          <>
            <input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} style={{ width: 200 }} data-testid="composer-sched-at" />
            <button type="button" className="btn primary" onClick={schedule} disabled={busy === "sched"} data-testid="composer-sched-btn">{busy === "sched" ? "…" : scheduled ? "💾 Salva modifiche" : "🕐 Programma"}</button>
          </>
        )}
        {!scheduled && <button type="button" className="btn primary" onClick={send} disabled={busy === "send"} data-testid="composer-send-btn" title={!toStr.trim() ? "Indica prima almeno un destinatario" : "Invia la mail"}>{busy === "send" ? "Invio…" : "Invia"}</button>}
      </div>
    </Modal>
  );
}
